import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import cn from 'classnames';
import {useBasket} from 'commerceAPI/hooks';
import {v4 as uuidv4} from 'uuid';

import Skeleton from 'coreComponents/Skeleton';
import {useLocalesMgr} from 'coreHooks';

import {useGeneralProvider} from 'brandComponents/_app-config/GeneralProvider';

import {addAmazonButton, addAmazonScript, fetchAmazonConfig, removeAmazonPayScript} from './helpers';
import {IAmazonPayButtonProps} from './IAmazonPayButton';

import styles from './AmazonPayButton.module.scss';

const AmazonPayButton = ({classNames, children}: IAmazonPayButtonProps) => {
	const [isLoaded, setIsLoaded] = useState(false);
	const [notApplicable, setNotApplicable] = useState(false);
	const [isCustomButton, setCustomButton] = useState(false);
	const mountedRef = useRef(true);
	const {managerRepository} = useGeneralProvider();
	// basket basketInitializationId
	const basket = useBasket();
	const basketInitializationId = basket?.getInitializationId();
	// country code
	const localesMgr = useLocalesMgr();
	const currentLocale = localesMgr.getCurrentLocale();
	const currentCountry = currentLocale.getCountryCode();
	const prevCountry = useRef(currentCountry);
	// unique DOM element id in case if we have more than 1 button on the page at a time
	const btnId = useMemo(() => `amazon-btn-${uuidv4()}`, []);
	const shippingMethodId = basket?.shipments?.[0]?.shippingMethod?.id;

	useEffect(() => {
		initAmazonPay();

		return () => {
			mountedRef.current = false;
			removeAmazonPayScript();
		};
	}, []);

	useEffect(() => {
		// If locale or basket changed, initialize Amazon Pay again.
		if (isLoaded && shippingMethodId) {
			removeAmazonPayScript();
			setNotApplicable(false);
			setCustomButton(false);
			initAmazonPay();
			prevCountry.current = currentCountry;
		}
	}, [
		currentCountry,
		basketInitializationId,
		shippingMethodId
	]);

	const initAmazonPay = useCallback(() => {
		let amazonConfig;

		fetchAmazonConfig(managerRepository)
			.then((config) => {
				if (mountedRef.current) {
					if (config && config.isAmazonPayButtonApplicable) {
						const scriptUrl = config.amazonPreferences.scriptURL;

						// Temporary fix in order to overwrite the new config for pwa, because minicart is not ready for release
						setCustomButton(true);
						amazonConfig = config;

						return addAmazonScript(scriptUrl);
					}

					// if Amazon pay is not applicable
					setNotApplicable(true);
				}
			})
			.then(() => {
				setIsLoaded(true);

				if (mountedRef.current && amazonConfig && (window as any).amazon) {
					addAmazonButton(amazonConfig.amazonPreferences, btnId);
				}
			});
	}, []);

	if (notApplicable) {
		return null;
	}

	return (
		<div className={cn(styles['amazon-pay'], 'amazon-pay__wrapper', classNames)}>
			<div
				id={btnId}
				className={cn(
					{[styles['amazon-pay__btn--customized']]: isCustomButton},
					{[styles['amazon-pay__btn--hidden']]: !isLoaded},
					'amazon-pay__btn',
				)}
			/>
			{!isLoaded && <Skeleton classNames="amazon-pay__loader" />}
			{isLoaded && isCustomButton && (
				<div className={cn(styles['amazon-pay__custom-content'], 'amazon-pay__custom-content')}>
					{children}
				</div>
			)}
		</div>
	);
};

export default AmazonPayButton;
